import { ContactsForm } from "@/components/contact/ContactsForm";
import HiddenInput from "@/components/form/components/HiddenInput";
import AntIcon from "@/components/icons/AntIcon";
import { selectTagsStandardProps } from "@/utils/componentUtils";
import { formatPhoneNumber } from "@/utils/formatUtils";
import { getAllFieldsNames, resolveFormValidationError } from "@/utils/formUtils";
import { dateToIsoDateString } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { App as AntApp, Button, Col, Divider, Form, Row, Select, Space } from "antd";
import { FormInstance } from "antd/lib/form";
import dayjs, { Dayjs } from "dayjs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { rowGutter } from "../../../../common/constants";
import { ContactType, MarketingConsent } from "../../../../common/enums";
import { Contact } from "../../../../common/types";
import { UUID } from "../../../../typings/global";
import { ClientType, clientTypeTMap } from "../../enums";
import { ClientSearchProps } from "../../hooks";
import {
  Client,
  CreateUpdateClient,
  CreateUpdateLegalClient,
  CreateUpdateNaturalClient,
  CreateUpdateSelfEmployedClient,
  LegalClient,
  NaturalClient,
} from "../../types";
import { processClientFormValues } from "../../utils";
import ClientTypeLongNameTag from "../tags/ClientTypeLongNameTag";
import { ClientAddressesFormPart } from "./parts/ClientAddressesFormPart";
import { ClientRepresentativesFormPart } from "./parts/ClientRepresentativesFormPart";
import { LegalClientDataFormPart } from "./parts/LegalClientDataFormPart";
import { NaturalClientDataFormPart } from "./parts/NaturalClientDataFormPart";
import { SelfEmployedClientDataFormPart } from "./parts/SelfEmployedClientDataFormPart";

const parseClientToCreateUpdateClient = ({
  historyRecords,
  attachments,
  assignedAgents,
  createdBy,
  ...client
}: Partial<Client>) => {
  const commonValues = {
    ...client,
    managedById: client.managedBy?.id ?? client.managedById,
    contacts: client.contacts?.map((contact) => ({
      ...contact,
      value:
        contact.type === ContactType.PHONE_NUMBER || contact.type === ContactType.MAIN_PHONE_NUMBER
          ? formatPhoneNumber(contact.value)
          : contact.value,
      marketingConsentStartDate: contact.marketingConsentStartDate,
      marketingConsentEndDate: contact.marketingConsentEndDate,
    })),
  };

  if (client.type === ClientType.LEGAL) {
    const legalValues: Partial<CreateUpdateLegalClient> = {
      ...commonValues,
      type: ClientType.LEGAL,
      representatives:
        (client as LegalClient).representatives?.map((r) => ({
          id: r.id,
          optimisticLockVersion: r.optimisticLockVersion,
          function: r.function,
          representativeId: r.representative.id,
          representativePin: r.representative.identifier,
        })) ?? [],
    };

    return legalValues as CreateUpdateLegalClient;
  }

  return commonValues as CreateUpdateClient;
};

type BasicDataProps = {
  type: ClientType;
  form: FormInstance<CreateUpdateClient>;
  disablePinInput?: boolean;
  disableCrnInput?: boolean;
  requireIdCardNumber?: boolean;
  clientSearch?: ClientSearchProps;
  viewType: "form" | "drawer";
  client?: Partial<Client>;
  isCreatingNewClient?: boolean;
};

const BasicClientData = ({
  type,
  form,
  disablePinInput,
  disableCrnInput,
  requireIdCardNumber,
  clientSearch,
  viewType,
  client,
  isCreatingNewClient,
}: BasicDataProps) => {
  const { t } = useTranslation();

  const handleCrnSearchOrChange = (crn: string) => {
    const type = form.getFieldValue("type") as ClientType;
    if (clientSearch && (type === ClientType.SELF_EMPLOYED || type === ClientType.LEGAL)) {
      form
        .validateFields(["companyRegistrationNumber"])
        .then(() => clientSearch.onSearch({ keyword: crn, clientType: type }))
        .catch(resolveFormValidationError);
    }
  };

  const colSpan = viewType === "form" ? 4 : 6;

  switch (type) {
    case ClientType.NATURAL:
      return (
        <NaturalClientDataFormPart
          form={form as FormInstance<CreateUpdateNaturalClient>}
          colSpan={colSpan}
          disablePinInput={disablePinInput}
          requireIdCardNumber={requireIdCardNumber}
          previousIdCardNumber={(client as NaturalClient)?.previousIdentityCardNumber}
          clientManagedById={
            client?.managedBy?.id ?? (client && "managedById" in client ? (client.managedById as UUID) : undefined)
          }
          isCreatingNew={isCreatingNewClient}
        />
      );
    case ClientType.SELF_EMPLOYED:
      return (
        <SelfEmployedClientDataFormPart
          form={form as FormInstance<CreateUpdateSelfEmployedClient>}
          colSpan={colSpan}
          disableCrnInput={disableCrnInput}
          requireIdCardNumber={requireIdCardNumber}
          previousIdCardNumber={(client as NaturalClient)?.previousIdentityCardNumber}
          searchInProgress={clientSearch?.inProgress}
          onCrnSearchOrChange={clientSearch ? handleCrnSearchOrChange : undefined}
          clientManagedById={
            client?.managedBy?.id ?? (client && "managedById" in client ? (client.managedById as UUID) : undefined)
          }
          isCreatingNew={isCreatingNewClient}
        />
      );
    case ClientType.LEGAL:
      return (
        <>
          <LegalClientDataFormPart
            colSpan={colSpan}
            disableCrnInput={disableCrnInput}
            searchInProgress={clientSearch?.inProgress}
            onCrnSearchOrChange={clientSearch ? handleCrnSearchOrChange : undefined}
            clientManagedById={
              client?.managedBy?.id ?? (client && "managedById" in client ? (client.managedById as UUID) : undefined)
            }
            isCreatingNew={isCreatingNewClient}
          />

          {viewType === "form" && (
            <>
              <Divider titlePlacement="start">{t("client.titles.representatives")}</Divider>
              <ClientRepresentativesFormPart form={form} initialClient={client as LegalClient} colSpan={colSpan} />
            </>
          )}
        </>
      );
    default:
      return null;
  }
};

interface Props {
  form: FormInstance<CreateUpdateClient>;
  client?: Partial<Client>;
  initialClientType?: ClientType;
  excludedClientTypes?: ClientType[];
  disablePinInput?: boolean;
  disableCrnInput?: boolean;
  requireIdCardNumber?: boolean;
  viewType: "form" | "drawer";
  clientSearch?: ClientSearchProps;
  onFormSubmit: (client: CreateUpdateClient) => void;
  onCancelClick?: () => void;
  onlyIdCardIdentityTypeSupport?: boolean;
  isCreatingNewClient?: boolean;
}

export const ClientFormBody = ({ form, client, clientSearch, ...props }: Props) => {
  const { t } = useTranslation();
  const { notification } = AntApp.useApp();

  const type = Form.useWatch("type", form);

  const enabledClientTypes: ClientType[] = props.excludedClientTypes
    ? Object.values(ClientType).filter((type) => !props.excludedClientTypes?.includes(type))
    : Object.values(ClientType);

  useEffect(() => {
    form.resetFields();

    if (client) {
      form.setFieldsValue(parseClientToCreateUpdateClient(client));
    }
  }, [client, form]);

  useEffect(() => {
    if (clientSearch?.result?.data) {
      const { keyword, clientType, data } = clientSearch.result;
      if (keyword === form.getFieldValue("companyRegistrationNumber") && clientType === form.getFieldValue("type")) {
        form.setFieldsValue(parseClientToCreateUpdateClient(data));
        if (data.id) {
          form.setFields([{ name: "companyRegistrationNumber", errors: [t("validation.unique")] }]);
        }
      }
    }
  }, [clientSearch?.result]);

  const handleClientTypeChange = () => {
    if (form.getFieldValue(["representatives"])?.length > 0) {
      form.setFieldsValue({ representatives: [] });
    }
  };

  const handleMarketingConsentChange = (consent: MarketingConsent, index: number) => {
    const contacts = [...form.getFieldValue("contacts")];
    contacts[index] = {
      ...contacts[index],
      marketingConsentStartDate:
        consent === MarketingConsent.GRANTED ? contacts[index]?.marketingConsentStartDate : undefined,
      marketingConsentEndDate:
        consent === MarketingConsent.GRANTED ? contacts[index]?.marketingConsentEndDate : undefined,
    };

    form.setFieldsValue({ contacts: contacts as Contact[] });
  };

  const handleMarketingConsentStartDateChange = (startDate: Dayjs, index: number) => {
    const contacts = [...form.getFieldValue("contacts")];
    contacts[index] = {
      ...contacts[index],
      marketingConsentEndDate: dateToIsoDateString(dayjs(startDate).add(5, "year")),
    };

    form.setFieldsValue({ contacts: contacts as Contact[] });
  };

  const handleFormSubmit = () => {
    if (props.viewType === "drawer") {
      props.onFormSubmit(processClientFormValues(form.getFieldsValue()));
    } else {
      let fieldsToValidate = getAllFieldsNames(form);

      const notGrantedMarketingConsentIndices = fieldsToValidate
        .filter(
          (fieldPath) =>
            // @ts-ignore ts-problem
            fieldPath.includes("marketingConsent") && form.getFieldValue(fieldPath) !== MarketingConsent.GRANTED,
        )
        .map((fieldPath) => fieldPath[1]);

      fieldsToValidate = fieldsToValidate.filter(
        (fieldPath) =>
          (!fieldPath.includes("marketingConsentStartDate") && !fieldPath.includes("marketingConsentEndDate")) ||
          !notGrantedMarketingConsentIndices.includes(fieldPath[1]),
      );

      fieldsToValidate = Object.values(form.getFieldValue("correspondenceAddress") ?? {}).some(
        (data) => data !== undefined,
      )
        ? fieldsToValidate
        : fieldsToValidate.filter((fieldPath) => !fieldPath.includes("correspondenceAddress"));

      form
        .validateFields(fieldsToValidate)
        .then((values) => {
          const processedValues = processClientFormValues(values);

          if (processedValues.type === ClientType.LEGAL) {
            const representatives = (values as CreateUpdateLegalClient).representatives || [];
            if (representatives.some((r) => !r.representativeId)) {
              notification.error({
                title: t("common.error"),
                description: t("client.validations.representativesError"),
              });
              return;
            }
            (processedValues as CreateUpdateLegalClient).representatives = representatives.map((r) => ({
              ...r,
              representativePin: undefined,
            }));
          }

          props.onFormSubmit(processedValues);
        })
        .catch(resolveFormValidationError);
    }
  };

  return (
    <>
      <HiddenInput name="optimisticLockVersion" />
      {client ? (
        <HiddenInput name="type" />
      ) : (
        <Row gutter={rowGutter}>
          <Col span={7}>
            <Form.Item
              name="type"
              label={t("client.enums.type._label")}
              rules={[validations.notNull]}
              initialValue={props.initialClientType || enabledClientTypes[0]}
            >
              <Select
                {...selectTagsStandardProps(clientTypeTMap)}
                disabled={!!props.initialClientType}
                options={enabledClientTypes.map((type) => ({
                  value: type,
                  label: <ClientTypeLongNameTag type={ClientType[type]} />,
                }))}
                onChange={handleClientTypeChange}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Divider titlePlacement="start">{t("client.titles.basicData")}</Divider>

      <BasicClientData
        form={form}
        type={type}
        disablePinInput={props.disablePinInput}
        disableCrnInput={props.disableCrnInput}
        requireIdCardNumber={props.requireIdCardNumber}
        clientSearch={clientSearch}
        viewType={props.viewType}
        client={client}
        isCreatingNewClient={props.isCreatingNewClient}
      />

      <Divider titlePlacement="start">{t("client.titles.addresses")}</Divider>

      <ClientAddressesFormPart hasCorrespondenceAddress={!!client?.correspondenceAddress} />

      <Divider titlePlacement="start">{t("client.titles.contacts")}</Divider>

      <ContactsForm
        marketingConsentApplicable
        onMarketingConsentChange={handleMarketingConsentChange}
        onMarketingConsentStartDateChange={handleMarketingConsentStartDateChange}
        hasMainContacts
      />

      <Space className="margin-top-large">
        <Button
          type="primary"
          icon={props.viewType === "form" ? <AntIcon type="save" /> : <AntIcon type="check" />}
          onClick={handleFormSubmit}
        >
          {props.viewType === "form" ? t("common.save") : t("common.submit")}
        </Button>

        {props.onCancelClick && (
          <Button type="default" onClick={props.onCancelClick} icon={<AntIcon type="close" />}>
            {t("common.cancel")}
          </Button>
        )}
      </Space>
    </>
  );
};
